import React, { useEffect, useState } from "react";
import "./MyProductProperties.scss";
import { Button, Rate, Image } from "antd";
import { CloseCircleOutlined, EnvironmentFilled } from "@ant-design/icons";
import PriceSlider from "@components/PriceSlider/PriceSlider";
import { IProductForm } from "@utils/Interfaces";
import { useUser } from "contexts/UserContext";
import Helpers from "@utils/Helpers";
import { errorLog } from "logging";
import { ProductApi } from "services/ProductApi";
import ProductProperties from "@components/ProductProperties/ProductProperties";
import { useUnit } from "contexts/UnitContext";
import ProductRegister from "@containers/ProductRegister/ProductRegister";

interface IMyProductProperties {
  data: any;
  selectedProductId: number | null;
  setSelectedProductId: (value: number | null) => void;
  setMyProducts: (value: any) => void;
}

const MyProductProperties: React.FC<IMyProductProperties> = ({
  data,
  setSelectedProductId,
  selectedProductId,
  setMyProducts,
}) => {
  const { units, convertCurrency } = useUnit();
  const { userProfile } = useUser();
  const [metrics, setMetrics] = useState(null);
  const [productForm, setProductForm] = useState<IProductForm>({
    product_id: 0,
    images: [],
    address: {
      street: "",
      city: "",
      province: "",
      zip_code: "",
      lat: 0,
      lon: 0,
    },
    product_category_id: 1,
    hay_type: "",
    hay_availability: "",
    shape: "ssquare",
    size: "",
    rain: "No",
    moisture: "",
    hay_compression: "",
    hay_density: "",
    weight_per_bale: "",
    total_weight: "",
    hay_cut: "1st cut",
    type_of_binding: "Twine",
    crop: "Old",
    storage: "Covered",
    farm_id: userProfile?.farm_id || null,
    selling_method: "fixed price",
    price_per_tons: 0,
    price_per_bale: 0,
    current_price: 0,
    hay_unit: "Bale",
    shipping_options_details: {},
    shipping_cost: 0,
    payment_info_ids: [],
    return_within: "7",
    return_shipping_by: "buyer",
    crude_protein: 0,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isReposting, setIsReposting] = useState(false);

  // HANDLE SET PRODUCT FORM
  useEffect(() => {
    if (data) {
      setProductForm({
        ...productForm,
        product_id: data.product_id,
        images: data.images,
        address: {
          ...productForm.address,
          city: data.city,
          province: data.province,
          zip_code: data.product_zip_code,
          lat: data.lat,
          lon: data.lon,
        },
        product_category_id: 1,
        hay_type: data.hay_type,
        hay_availability: data.quantity,
        shape: data.shape,
        size: data.size,
        rain: data.rain ? "Yes" : "No",
        moisture: data.moisture,
        hay_compression: data.hay_compression,
        hay_density: data.hay_density,
        weight_per_bale: data.bale_weight,
        total_weight: data.total_weight,
        hay_cut: data.hay_cut,
        type_of_binding: data.type_of_binding,
        crop: data.crop,
        storage: data.storage,
        price_per_tons: data.price_per_tons,
        price_per_bale: data.price_per_bale,
        current_price: data.current_price,
        hay_unit: data.unit,
        shipping_options_details: data.shipping_option_details,
      });
    }
  }, [data]);

  // HANDLE FETCH METRICS
  useEffect(() => {
    const fetchMetric = async () => {
      try {
        const response = await ProductApi.GetMetric(productForm.hay_type);
        if (response.status === 200) {
          setMetrics(response.data.data);
        }
      } catch (error) {
        Helpers.notification.error("Can not fetch metrics");
        errorLog("ProductRegister.tsx - fetchMetric", error);
      }
    };
    if (productForm.hay_type !== "") {
      fetchMetric();
    }
  }, [productForm.hay_type]);

  const handleRepost = async () => {
    try {
      const response = await ProductApi.RePostProduct({
        product_id: data.product_id,
      });
      if (response.status === 200) {
        setSelectedProductId(null);
        setMyProducts(null);
      }
    } catch (error) {
      Helpers.notification.error("Something wrong in server");
      errorLog("MyProductProperties.tsx - handleRepost", error);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await ProductApi.DeleteProduct({
        product_ids: [data.product_id],
      });
      if (response.status === 200) {
        setSelectedProductId(null);
        setMyProducts(null);
      }
    } catch (error) {
      Helpers.notification.error("Something wrong in server");
      errorLog("MyProductProperties.tsx - handleRepost", error);
    }
  };

  return (
    <div className={`MyProductProperties ${selectedProductId && "display"}`}>
      {isEditing || isReposting ? (
        <>
          <ProductRegister
            data={productForm}
            context={isEditing ? "Edit" : "Repost"}
          />
        </>
      ) : (
        <div className="product-info">
          <div className="prop-header">
            <div className="prop-status">Posted/Selling</div>
            <div className="prop-category">Hay</div>
            <div className="prop-close">
              <Button
                className="prop-close-btn"
                onClick={() => setSelectedProductId(null)}
                icon={<CloseCircleOutlined />}
                size="large"
                type="link"
                color="default"
              />
            </div>
          </div>
          <div className="prop-main-info">
            <div className="prop-main">
              <div className="prop-product-name">{productForm.hay_type}</div>
              <div className="prop-product-rating">
                <Rate disabled defaultValue={4} />
                <span className="ms-1">0 reviews</span>
              </div>
              <div className="prop-product-location">
                <EnvironmentFilled /> {productForm.address.province}
              </div>
            </div>
            <div className="prop-price">
              $
              {Helpers.formatNumber(
                convertCurrency(
                  productForm.hay_unit == "Ton"
                    ? productForm.price_per_tons
                    : productForm.price_per_bale,
                  "USD",
                  units.currency
                ),
                2
              )}{" "}
              / {productForm.hay_unit}
            </div>
          </div>
          <div className="prop-slider">
            <PriceSlider
              unit={1}
              type={"myProduct"}
              productForm={productForm}
              setProductForm={setProductForm}
              metrics={metrics}
            />
          </div>
          <div className="prop-product-properties">
            <ProductProperties data={productForm} type="My Product" />
          </div>
          <div className="prop-images">
            {productForm.images.map((item, index) => {
              return (
                <Image
                  key={index}
                  src={item}
                  className="product-image"
                  width={50}
                  height={50}
                  preview={false}
                >
                  T
                </Image>
              );
            })}
          </div>
          <div className="prop-control">
            <Button
              className="delete-btn"
              onClick={() => {
                Helpers.confirmModal(
                  "This product will be deleted and you can not restore. Are you sure?",
                  handleDelete
                );
              }}
            >
              Delete
            </Button>
            <Button className="live-btn">Live View</Button>
            <Button className="edit-btn" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
            <Button
              className="sell-btn"
              onClick={() => {
                setIsReposting(true);
                // Helpers.confirmModal(
                //   "The new product will be created base on this product. Are you sure?",
                //   handleRepost
                // );
              }}
            >
              Sell Similar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProductProperties;
